import { faroCoreApiClient } from "@api/faro-core-api-client";
import { createAsyncThunk } from "@reduxjs/toolkit";

/** Payload returned by the group details thunk, including the full projects for the projectsMap */

/**
 * Fetches the list of groups that a company member belongs to.
 * @throws {Error} if there is no selected company ID
 * @throws {HttpUtils.FetchError} if the Core API client request fails
 */
export const fetchCompanyMemberGroups = createAsyncThunk("companyMemberGroups/fetchCompanyMemberGroups", async ({
  memberId
}, {
  getState
}) => {
  const {
    sdbCompany: {
      selectedSdbCompanyId
    }
  } = getState();
  if (!selectedSdbCompanyId) {
    throw new Error("No companyId was given to fetch company member groups");
  }
  return await faroCoreApiClient.groups.getCompanyMemberGroups({
    companyId: selectedSdbCompanyId,
    userIdentity: memberId
  });
}, {
  condition: (_, api) => {
    // Skip fetching if groups have already been fetched
    return api.getState().companyMemberGroups.status === "uninitialized";
  }
});

/**
 * Fetches the details (including projects) for a single group that a member belongs to.
 * Stores the result as a `CompanyMemberGroupEntity` in the entity adapter.
 *
 * @throws {Error} if there is no selected company ID
 * @throws {HttpUtils.FetchError} if the Core API client request fails
 */
export const fetchCompanyMemberGroupDetails = createAsyncThunk("companyMemberGroups/fetchCompanyMemberGroupDetails", async ({
  groupId,
  userIdentity
}, {
  getState
}) => {
  const {
    sdbCompany: {
      selectedSdbCompanyId
    }
  } = getState();
  if (!selectedSdbCompanyId) {
    throw new Error("No companyId was given to fetch company member groups");
  }
  const data = await faroCoreApiClient.groups.getCompanyMemberGroupDetails({
    companyId: selectedSdbCompanyId,
    groupId,
    userIdentity
  });
  return {
    projectIds: data.projects.map(project => project.id),
    projects: data.projects
  };
});